import {
  Button,
  ButtonGroup,
  DataStatus,
  DialogFooter,
  openDialog,
  Option,
} from 'platform/components';
import {Box, Hide, HStack, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';
import {isArray, isString} from 'ramda-adjunct';

import {useGetAllTireWarehousesQuery, useUpdateTireSetLocationMutation} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps} from 'shared/web';

import type {RowData} from 'features/eag-grid';

import {handleApiError} from '../../../utils/handleApiError';
import {refreshDmsDatagrid} from '../../../utils/refreshDmsDatagrid';
import {CreateTireLocation} from '../../CreateTireLocation/CreateTireLocation';
import {DmsDataGrid} from '../../DmsDataGrid/DmsDataGrid';
import {StorageLocationFilters} from './StorageLocationFilters';

interface StorageWarehouseGridRow extends RowData {
  storageLocation: {value: string};
}

const EXTERNAL_FILTER_ID = 'assign-storage-location-tire-warehouse-external-filter';

interface StorageWarehouseDialogProps extends RequiredTestIdProps {
  tireSetId?: string | Nullish;
  tireOrderId: string;
  isStockingTires?: boolean;
  onClose: () => void;
}

export function StorageWarehouseDialog(props: StorageWarehouseDialogProps) {
  const [selectedLocation, setSelectedLocation] = useState<StorageWarehouseGridRow>();
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>();

  const [updateTireSetLocation, updateTireSetLocationStatus] = useUpdateTireSetLocationMutation();

  const warehouseQuery = useGetAllTireWarehousesQuery();

  const warehouseOptions: Option[] =
    warehouseQuery.data?.map((warehouse) => ({
      label: warehouse.name,
      value: warehouse.warehouseId,
    })) || [];
  const tireWarehouseId = selectedWarehouseId ?? warehouseOptions[0]?.value;

  const handleCreateSingleLocation = () => {
    if (isNil(tireWarehouseId)) {
      return;
    }

    openDialog(
      <CreateTireLocation
        dialogId="createTireLocationDialog"
        tireWarehouseId={tireWarehouseId}
        onSuccess={() => refreshDmsDatagrid('assign-storage-location-tire-warehouse')}
      />,
      {
        id: 'createTireLocationDialog',
        title: i18n.t('entity.tireWarehouses.labels.createSingleLocation'),
      }
    );
  };

  const handleLocationSelection = () => {
    if (!props.tireSetId || !selectedLocation) {
      return;
    }

    const locationData = {
      location: selectedLocation.storageLocation.value,
      locationDefinition: {
        contextTarget: 'STORAGE_LOCATION',
        contextId: selectedLocation.id,
      },
    } as const;

    updateTireSetLocation({
      orderId: props.tireOrderId,
      setId: props.tireSetId,
      body: locationData,
    })
      .unwrap()
      .then(() => {
        refreshDmsDatagrid('tire-order-set');

        props.onClose();
      })
      .catch(handleApiError);
  };

  return (
    <VStack minHeight={50}>
      <DataStatus
        minHeight={50}
        isLoading={warehouseQuery.isLoading}
        isError={warehouseQuery.isError}
        isEmpty={!warehouseOptions?.length || !warehouseQuery.data?.length}
        emptyMessage={i18n.t('entity.tireWarehouses.labels.noWarehouses')}
      >
        <VStack spacing={4}>
          <HStack align="center" justify="space-between">
            <div id={EXTERNAL_FILTER_ID} />
            <Box paddingTop={4}>
              <ButtonGroup>
                <Button
                  title={i18n.t('entity.tireWarehouses.labels.createSingleLocation')}
                  variant="secondary"
                  leftIcon="content/add_circle"
                  onClick={handleCreateSingleLocation}
                  isDisabled={isNil(tireWarehouseId)}
                />
              </ButtonGroup>
            </Box>
          </HStack>
          <Box minHeight={50} flex={1} paddingBottom={16}>
            <DmsDataGrid
              gridCode="assign-storage-location-tire-warehouse"
              queryModifier={(filter) => {
                if (isNil(filter) || isNil(filter.tireWarehouse)) {
                  return {
                    ...filter,
                    tireWarehouse: [warehouseOptions[0]?.value],
                  };
                }

                return filter;
              }}
              onExternalFilterChange={(filters) => {
                const [warehouseId]: unknown[] = isArray(filters.tireWarehouse)
                  ? filters.tireWarehouse
                  : [];

                setSelectedWarehouseId(isString(warehouseId) ? warehouseId : undefined);
              }}
              filterComponent={(filterProps) => (
                <StorageLocationFilters {...filterProps} warehouseOptions={warehouseOptions} />
              )}
              externalFilterId={EXTERNAL_FILTER_ID}
              onRowSelectionChange={(selection) => {
                setSelectedLocation(selection[0] as StorageWarehouseGridRow);
              }}
              autoHeight
            />
          </Box>
          <DialogFooter>
            <ButtonGroup align="right">
              <Hide when={props.isStockingTires}>
                <Button
                  title={i18n.t('general.labels.discard')}
                  variant="secondary"
                  onClick={props.onClose}
                  isDisabled={updateTireSetLocationStatus.isLoading}
                />
              </Hide>
              <Button
                title={
                  props.isStockingTires
                    ? i18n.t('entity.tireSet.actions.stockingTireSet')
                    : i18n.t('general.labels.assign')
                }
                isDisabled={!selectedLocation}
                onClick={handleLocationSelection}
                isLoading={updateTireSetLocationStatus.isLoading}
              />
            </ButtonGroup>
          </DialogFooter>
        </VStack>
      </DataStatus>
    </VStack>
  );
}
